// PARCEIRO FIODECOBRE — a aba "Funcionamento" (DA-445).
//
// O programa inteiro foi decidido em conversa ao longo de 4 dias e mora hoje em cinco ADRs
// (DA-435/436/437/444/445) + memoria. Nada disso e' alcancavel por quem opera a loja — e o
// programa e' justamente o que a equipe mais vai ter que explicar em voz alta, pro parceiro que
// pergunta "e quanto eu recebo?" e pro contador dele. Esta aba e' esse documento, dentro do ERP.
//
// ⚠️ Le os PARAMETROS VIVOS (recebidos por prop): um documento que repete "15%" em texto fixo vira
// mentira no dia em que a aliquota muda na aba ao lado, e ninguem lembra de vir corrigir aqui.

import { formatMoeda } from '@fiodecobre/shared';

interface Props {
  custoPct: string;
  tetoMarkupPct: string;
  tetoUsoPct: string;
}

export function ParceiroFuncionamento({ custoPct, tetoMarkupPct, tetoUsoPct }: Props) {
  const custo = Number(String(custoPct).replace(',', '.'));
  const pctCusto = Number.isFinite(custo) ? custo : 15;
  const semTetoMarkup = String(tetoMarkupPct).trim() === '';
  const semTetoUso = String(tetoUsoPct).trim() === '';

  // O exemplo canonico, recalculado com a aliquota vigente — e' assim que ele explica pro parceiro.
  const exBase = 1000;
  const exInformado = 1200;
  const exDif = exInformado - exBase;
  const exCusto = Math.round((exDif * pctCusto) / 100);
  const exCredito = exDif - exCusto;
  // Formatador da casa (com separador de milhar) — o documento nao pode escrever dinheiro num
  // formato que nenhuma outra tela do sistema usa.
  const real = (v: number) => formatMoeda(v);

  return (
    <div style={{ maxWidth: 860, display: 'grid', gap: 18, fontSize: 13.5, lineHeight: 1.65 }}>
      <Bloco titulo="A ideia, em uma frase">
        <p style={p}>
          O parceiro — eletricista, instalador, prestador de servico — passa um orcamento ao cliente dele com
          mao de obra <b>e</b> material. Ele quer ganhar tambem no material, porque esta cotando, buscando e
          respondendo pela troca, e isso custa. Mas o cliente nao quer pagar a mais pela compra.
        </p>
        <p style={p}>
          O programa resolve isso <b>sem tirar da margem da loja</b>: o parceiro informa ao cliente dele um
          valor acima do que o material custa, a nota sai pelo valor cheio, entra o valor cheio no caixa, e a
          loja devolve a diferenca a ele como <b>credito de loja</b> — resgatavel so aqui.
        </p>
        <Exemplo>
          <Linha rotulo="Material que ele leva" valor={real(exBase)} />
          <Linha rotulo="Valor que ele informa ao cliente dele" valor={real(exInformado)} />
          <Linha rotulo="A nota sai por" valor={real(exInformado)} nota="e entram os mesmos no caixa" />
          <Linha rotulo="Diferenca" valor={real(exDif)} />
          <Linha rotulo={`Custo da Operacao (${pctCusto}%)`} valor={`− ${real(exCusto)}`} />
          <Linha rotulo="Credito pro parceiro" valor={real(exCredito)} destaque />
        </Exemplo>
        <p style={p}>
          <b>Por que isso ganha de um desconto simples:</b> desconto sai da margem da loja — numa venda de mil
          reais ela aguenta uns R$ 40. O markup sai do bolso do cliente <i>dele</i>, e sao R$ 200 invisiveis.
          Cinco vezes maior, nao custa nada a loja, e desconto nao prende ninguem: credito parado aqui, prende.
        </p>
      </Bloco>

      <Bloco titulo="Por que o parceiro aceita credito em vez de embolsar o dinheiro">
        <p style={p}>
          Essa e' a pergunta que sustenta o programa inteiro — afinal ele poderia marcar os mesmos R$ 200 no
          orcamento de servico dele e receber em dinheiro. A resposta e' a <b>nota</b>:
        </p>
        <Citacao>
          "Quase que sempre o cliente pede a nota. E mesmo quando nao pede, o parceiro quer apresentar a nota,
          como uma forma de prestacao de contas bem esclarecida."
        </Citacao>
        <p style={p}>
          Ou seja: o programa <b>nao</b> e "ajudamos voce a esconder seu ganho". A nota vira o instrumento de
          credibilidade dele — ele mostra o documento, o documento bate com o que falou, e a conversa com o
          cliente acaba ali. <b>Ele nao consegue produzir isso sozinho; so a loja produz.</b> E' por isso que
          ha produto.
        </p>
        <p style={p}>
          Duas consequencias praticas que isso cria: a nota vai ser <b>lida por um terceiro com atencao</b>,
          quase toda vez — entao os precos tem que sair criveis (nada de valor esquisito ou de um item inflado
          sozinho) — e a nota <b>nao pode carregar nenhum traco do programa</b>.
        </p>
      </Bloco>

      <Bloco titulo="O caminho da venda, passo a passo">
        <ol style={{ ...p, paddingLeft: 20, display: 'grid', gap: 8 }}>
          <li>
            <b>A adesao.</b> Voce marca o selo na ficha do cliente (Arquivos &rarr; Clientes). <b>So voce
            marca</b> — e adesao, nao cadastro corriqueiro. O cliente precisa ter <b>WhatsApp cadastrado</b>,
            senao o sistema recusa: o saldo so se consulta por la, e sem canal o credito nasce e o parceiro
            nunca ve.
          </li>
          <li>
            <b>O orcamento.</b> Qualquer vendedora monta a compra normalmente e escolhe o parceiro no campo
            <b> Parceiro</b>, ao lado do "Retirado por". A lista so oferece quem tem o selo.
          </li>
          <li>
            <b>O valor informado.</b> A vendedora digita no campo <b>Liquido</b> o valor que o parceiro falou
            ao cliente. O sistema espalha a diferenca <b>pelos itens</b>, proporcionalmente — nao existe linha
            "markup" na nota, e nao pode existir.
          </li>
          <li>
            <b>O caixa.</b> O pedido chega ja valendo o valor informado. Antes de finalizar, a tela mostra a{' '}
            <b>previa do premio</b>, pra o operador poder dizer em voz alta quanto vai sobrar pro parceiro.
          </li>
          <li>
            <b>O credito nasce no fechamento</b> — nunca antes. Nesse ponto o dinheiro ja entrou, e e' isso que
            autoriza a loja a devolver.
          </li>
          <li>
            <b>O resgate.</b> Noutra compra, o parceiro usa o saldo. A nota dessa compra sai com{' '}
            <b>"Desconto" liso</b>, sem citar origem nem numero de credito.
          </li>
        </ol>
      </Bloco>

      <Bloco titulo="As regras do dinheiro">
        <Regra titulo="A base e o preco da forma de pagamento, congelado">
          Pix usa o preco com desconto; cartao usa o preco de tabela. Cada item guarda os dois precos no
          momento em que o pedido vai ao caixa, entao a conta nao depende de a vendedora ter lembrado de dar o
          desconto do pix — e nao da pra inflar a base nem por desconto nem por markup. Pagamento misto usa a
          mesma formula que o caixa ja aplica.
          <br />
          <Atencao>
            Efeito aceito de olho aberto: como a base do pix e' menor, <b>o mesmo valor informado gera mais
            credito no pix</b> — e quem escolhe a forma e' o proprio parceiro. Ele vai empurrar pix.
          </Atencao>
        </Regra>

        <Regra titulo="O valor e ABSOLUTO, nunca percentual">
          O parceiro informa "R$ 1.200", nao "20%". A nota tem que bater exatamente com a palavra que ele deu
          ao cliente — e' essa a razao de o programa existir.
        </Regra>

        <Regra titulo={`Custo da Operacao: ${pctCusto}%`}>
          A retencao da loja sobre a diferenca. <b>Nao chamar de imposto</b> na frente dele: o parceiro tem
          contador e faz conta, e uma retencao que nao confere com a aliquota real derruba a confianca de um
          programa que so vive de confianca. "Custo da Operacao" e' defensavel em qualquer conversa.
          <br />
          <Atencao>
            Ha uma pergunta em aberto pro contador que decide se esses {pctCusto}% sao custo legitimo ou se
            se recuperam sozinhos: <b>desconto concedido reduz a receita bruta no PGDAS?</b> Enquanto nao vier
            a resposta, reter e' a posicao prudente.
          </Atencao>
        </Regra>

        <Regra titulo={semTetoMarkup ? 'Teto de markup: sem limite (hoje)' : `Teto de markup: ${tetoMarkupPct}%`}>
          {semTetoMarkup ? (
            <>
              Nenhum limite configurado — o parceiro pode informar o valor que quiser. Quando voce puser um
              numero na aba Geral, o pedido que passar do teto <b>nao vai ao caixa</b>, e a vendedora ve na
              hora qual e' o valor maximo permitido.
              <br />
              <Atencao>
                O teto morde <b>muito mais forte no pedido pequeno</b>: uma compra de R$ 4,00 fechada em
                R$ 5,00 ja e' 25%. Vale calibrar pela distribuicao real que a aba Geral mostra, nao por
                palpite.
              </Atencao>
            </>
          ) : (
            <>
              O maximo que o parceiro pode inflar sobre o preco real. Passou disso, o pedido <b>nao vai ao
              caixa</b> e a vendedora ve o valor maximo permitido. A trava fica no envio ao caixa de proposito
              — no fechamento seria tarde, porque o dinheiro ja entrou e a venda ficaria presa no balcao.
            </>
          )}
        </Regra>

        <Regra titulo={semTetoUso ? 'Teto de uso do credito: sem limite (hoje)' : `Teto de uso do credito: ${tetoUsoPct}%`}>
          Quanto do credito cabe numa compra. Existe por dois motivos: venda paga 100% em credito <b>nao emite
          nota</b>, e sem nota o credito seguinte nao tem onde virar desconto; e porque o que traz o parceiro
          de volta e' <b>saldo parado</b> — a compra tem que deixar sobra, senao o premio vira desconto comum,
          que nao prende. Vale so pro credito do programa; o de troca e' dinheiro do proprio cliente voltando
          e nao se limita.
        </Regra>

        <Regra titulo="O credito nao vence">
          Validade briga com o mecanismo: o que prende e' saldo parado, e credito que expira vira sensacao de
          perda num programa que vive de confianca.
        </Regra>
      </Bloco>

      <Bloco titulo="O que o sistema NAO deixa fazer (e por que)">
        <Regra titulo="Pedido de parceiro nao aceita Crediario">
          O credito so nasce sobre dinheiro que ja entrou. Toda outra forma de pagamento passa por uma trava
          que confirma o recebimento — o crediario e' literalmente a unica que fica de fora. Sem esta regra, um
          fiado geraria premio sobre venda que a loja ainda nao recebeu.
        </Regra>

        <Regra titulo="Pedido de parceiro nao aceita Credito da Loja">
          Foi o achado que salvou o programa de se autodestruir. Numa cesta de R$ 1.000 informada em R$ 1.200,
          se ele gastasse R$ 200 de credito a nota fecharia em <b>R$ 1.000</b> — e ele entregaria ao cliente,
          como prestacao de contas, um papel dizendo mil depois de ter dito mil e duzentos. Justamente na
          operacao que ele mais faria: acumular num servico e queimar no seguinte.
        </Regra>

        <Regra titulo="Uma compra aceita um credito so">
          Usou credito de troca, nao aceita do programa — e vice-versa. O operador escolhe no caixa, com os
          dois saldos na tela. Some o caso misto e, com ele, a pergunta "qual dos dois a nota deve citar".
          <br />
          <Atencao>
            Por isso o maximo abativel numa compra e' <b>o maior saldo, nunca a soma dos dois</b>.
          </Atencao>
        </Regra>

        <Regra titulo='A nota nunca diz "credito de troca" quando o credito e do programa'>
          Sai <b>"Desconto"</b> liso, sem origem e sem numero. O rastro nao se perde — continua na movimentacao
          e no extrato —, so nao vai pro papel que o cliente dele vai ler.
        </Regra>

        <Regra titulo='Nunca no cliente "Consumidor"'>
          Credito lancado no generico viraria carteira compartilhada que qualquer um gasta. O campo Parceiro
          recusa o Consumidor.
        </Regra>
      </Bloco>

      <Bloco titulo="Como o parceiro consulta o saldo">
        <p style={p}>
          <b>Sem envio automatico.</b> Ele consulta quando quiser, mandando a palavra <b>extrato</b> pro
          WhatsApp da loja. Vem a lista de debitos e creditos, e o Custo da Operacao aparece como <b>linha
          propria</b> (acrescimo {real(exDif)} − custo {real(exCusto)} = credito {real(exCredito)}), nao so o
          liquido — e' o que faz o nome ser honesto em vez de retencao descoberta por acidente.
        </p>
        <Atencao>
          So funciona no <b>numero de Atendimento da loja</b>. Varios parceiros falam pelo numero pessoal
          (inbox Compras) e ali a palavra "extrato" <b>nao faz nada</b> — nem responde, nem da erro. Por isso
          "escreve <i>extrato</i> pro numero da loja" tem que fazer parte do convite.
        </Atencao>
      </Bloco>

      <Bloco titulo="Comissao e meta da loja">
        <p style={p}>
          A comissao da vendedora sai sobre o <b>valor original</b>, nunca sobre o total inflado — o markup e'
          dinheiro do cliente do parceiro passando pela loja, e ninguem vendeu nada a mais.
        </p>
        <p style={p}>
          Em compensacao, o credito do programa <b>comissiona quando e' gasto</b>. E' o unico credito que faz
          isso, e a razao e' precisa: so no resgate existe uma vendedora pra creditar, e aquele material sairia
          da prateleira sem entrar na conta de ninguem. O credito de <b>troca</b> continua fora, porque ele ja
          foi comissionado na venda que o gerou — pagar de novo seria pagar duas vezes.
        </p>
        <p style={{ ...p, fontWeight: 600 }}>
          A frase que resume, e e' como se explica pra equipe: <i>a comissao soma o material que saiu, a preco
          de pix.</i>
        </p>
        <Atencao>
          Efeito de calendario: como o credito nao vence, <b>o mes do resgate e' o que ganha o volume</b> —
          markup gerado em agosto pode contar na meta so em dezembro, ou nunca, se ele nao gastar. Isso mexe no
          gatilho do bonus semanal.
        </Atencao>
      </Bloco>

      <Bloco titulo="O que ainda esta em aberto">
        <ul style={{ ...p, paddingLeft: 20, display: 'grid', gap: 6 }}>
          <li>
            <b>A primeira venda real.</b> O caminho inteiro funciona, mas nenhuma perna rodou com dinheiro de
            verdade ainda. Enquanto isso nao acontece, mudar qualquer regra sai de graca — nao ha historico a
            corrigir.
          </li>
          <li>
            <b>Como recrutar.</b> O pitch abre pela <b>seguranca, nunca pelo dinheiro</b>: "seu cliente e
            atendido na hora, com o preco que voce falou, e se faltar eu resolvo — voce nao passa vergonha".
            Chamar de <b>parceiro</b>, nunca de comissao. Apresentar pessoalmente, um a um.
          </li>
          <li>
            <b>A pergunta do contador</b> sobre o PGDAS (acima), que decide o destino do Custo da Operacao.
          </li>
          <li>
            <b>O numero do teto de markup</b>, que sai da distribuicao real — visivel na aba Geral assim que
            houver vendas.
          </li>
        </ul>
      </Bloco>

      <Bloco titulo="Uma peca pronta que ninguem usa">
        <p style={p}>
          Metade da dor do parceiro e' assumir a troca quando sobra ou vem errado — e essa metade <b>o sistema
          ja resolve hoje</b>, sem nada novo: o modulo de Trocas devolve material e gera credito.{' '}
          <b>"Sobrou material da obra? Traz de volta que vira credito seu"</b> custa zero, tira um risco real
          do ombro dele, devolve mercadoria pro estoque e alimenta <b>o mesmo saldo que prende</b>. Anda junto
          com o markup; nao substitui.
        </p>
      </Bloco>
    </div>
  );
}

const p = { margin: '0 0 10px' } as const;

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: 'var(--primary)' }}>{titulo}</h3>
      {children}
    </section>
  );
}

function Regra({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{titulo}</div>
      <div style={{ color: 'var(--text-muted)' }}>{children}</div>
    </div>
  );
}

// Amarelo+preto: o unico destaque que o Ruan distingue com seguranca (daltonismo verde/vermelho).
function Atencao({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: '#fde68a', color: '#000', borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 12.5, lineHeight: 1.55 }}>
      {children}
    </div>
  );
}

function Citacao({ children }: { children: React.ReactNode }) {
  return (
    <blockquote style={{ margin: '0 0 10px', paddingLeft: 12, borderLeft: '3px solid var(--primary)', fontStyle: 'italic', color: 'var(--text-muted)' }}>
      {children}
    </blockquote>
  );
}

function Exemplo({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', margin: '4px 0 12px', background: 'var(--panel-bg)' }}>
      {children}
    </div>
  );
}

function Linha({ rotulo, valor, nota, destaque }: { rotulo: string; valor: string; nota?: string; destaque?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0',
      fontWeight: destaque ? 700 : 400,
      borderTop: destaque ? '1px solid var(--border)' : undefined,
      marginTop: destaque ? 4 : undefined,
      paddingTop: destaque ? 7 : undefined,
    }}>
      <span>
        {rotulo}
        {nota && <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}> — {nota}</span>}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{valor}</span>
    </div>
  );
}
